import React, { createContext, useContext, useMemo } from 'react';
import { shellAPI } from '../services/api';
import { usePolling, useSecondsSince } from '../hooks/usePolling';
import { DEFAULT_TIME_ZONE, accountTimeZone, setAccountTimeZone, timeZoneAbbreviation, timeZoneLabel } from '../design/timeZone';
import { setAccountTerms, terms as accountTerms } from '../design/terms';

const SHELL_POLL_MS = 60000;

export function relativeAge(seconds) {
  if (seconds < 60) return 'just now';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours} h ago`;
  return `${Math.round(hours / 24)} days ago`;
}

const ShellStatusContext = createContext({ orgName: null, status: null, error: null, timeZone: DEFAULT_TIME_ZONE, terms: accountTerms() });

/**
 * Polls GET /api/shell/status for the organization name, the account time zone and community terms, call-record sync
 * time and nav attention counts. The zone and terms are set before children render, so formatters read them at call time.
 */
export function ShellStatusProvider({ children }) {
  const { data, error, refresh } = usePolling(() => shellAPI.getStatus(), SHELL_POLL_MS);
  const timeZone = data ? setAccountTimeZone(data.timeZone) : accountTimeZone();
  const terms = data ? setAccountTerms(data.terms) : accountTerms();
  const value = useMemo(() => ({ orgName: data?.accountName || null, status: data, error, timeZone, terms, refresh }), [data, error, timeZone, terms, refresh]);
  return <ShellStatusContext.Provider value={value}>{children}</ShellStatusContext.Provider>;
}

export function useShellStatus() {
  return useContext(ShellStatusContext);
}

/** The account's word for a community: { community, communities, communityLower, communitiesLower, aCommunity, count }. */
export function useTerms() {
  return useShellStatus().terms || accountTerms();
}

/** The account time zone; components that show dates or zone labels call it so they re-render when it loads. */
export function useAccountTimeZone() {
  const { timeZone } = useShellStatus();
  return useMemo(() => ({ zone: timeZone, label: timeZoneLabel(timeZone), abbreviation: timeZoneAbbreviation(timeZone) }), [timeZone]);
}

/** Full organization name for the nav organization switcher ("Leland Management", initials "LM"), as in the mockups. */
export function orgFullName(status) {
  return status?.accountFullName || status?.accountName || 'Your organization';
}

/** Filter-bar scope for subtitles: the selection's label, else "All of Leland Management". */
export function scopeLabel(hierarchy, status) {
  return hierarchy.filter.nodeId || hierarchy.filter.communityIds.length ? hierarchy.label : `All of ${orgFullName(status)}`;
}

/** Organization name for copy such as "How Leland Management's phones performed". */
export function useOrgName() {
  return useShellStatus().orgName || 'your organization';
}

/** "Importing call history · 12 of 90 days", or "· 12 days imported" when the window is unknown; null without an import. */
export function importLine(historyImport) {
  if (!historyImport) return null;
  const done = Number(historyImport.completedDays) || 0;
  const total = Number(historyImport.totalDays) || 0;
  if (total > 0) return `Importing call history · ${done} of ${total} ${total === 1 ? 'day' : 'days'}`;
  return `Importing call history · ${done} ${done === 1 ? 'day' : 'days'} imported`;
}

export function useSyncLine() {
  const { status, error } = useShellStatus();
  const last = status?.lastCallRecordSyncAt ? new Date(status.lastCallRecordSyncAt) : null;
  const seconds = useSecondsSince(last && !Number.isNaN(last.getTime()) ? last : null);
  const importing = importLine(status?.historyImport);
  if (importing) return { text: importing, healthy: true };
  if (seconds != null) return { text: `Call records synced ${relativeAge(seconds)}`, healthy: true };
  if (error) return { text: 'Sync status unavailable', healthy: false };
  if (status) return { text: 'No call records synced yet', healthy: false };
  return { text: 'Checking call record sync…', healthy: false };
}
